import React, { useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { enablePush } from "../lib/push";

const ASKED_KEY = "heldall_push_asked";

export function markPushAsked() {
  try { localStorage.setItem(ASKED_KEY, "1"); } catch (_) {}
}
export function hasAskedPush() {
  try { return localStorage.getItem(ASKED_KEY) === "1"; } catch (_) { return false; }
}

// Tasteful, gesture-triggered permission prompt. Never shown on cold load.
export default function PushPrompt({ open, onClose, onEnabled }) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const turnOn = async () => {
    setBusy(true);
    markPushAsked();
    try {
      const res = await enablePush();
      if (res.granted) {
        toast.success("Notifications on. I'll bring things back to you.");
        onEnabled && onEnabled();
      } else {
        toast("No problem. You can turn this on later in Settings.");
      }
    } catch (e) {
      toast("Couldn't turn on notifications. You can try again in Settings.");
    } finally {
      setBusy(false);
      onClose && onClose();
    }
  };

  const notNow = () => { markPushAsked(); onClose && onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center" data-testid="push-prompt">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={notNow} />
      <div className="relative w-full md:max-w-sm bg-[var(--surface)] rounded-t-3xl md:rounded-3xl border border-[var(--line)] shadow-[0_-8px_40px_rgba(20,32,27,0.18)] md:shadow-[0_20px_60px_rgba(20,32,27,0.22)] p-6 pb-7 fade-up">
        <button onClick={notNow} className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center text-[var(--ink4)] hover:bg-[var(--surface2)]" data-testid="push-prompt-close">
          <X size={16} />
        </button>
        <span className="inline-flex h-11 w-11 rounded-full bg-[var(--pine-soft)] items-center justify-center text-[var(--pine)] mb-3">
          <Bell size={20} strokeWidth={1.9} />
        </span>
        <h3 className="font-serif-display text-xl text-[var(--ink)] mb-1.5">Want Heldall to bring things back to you?</h3>
        <p className="text-sm text-[var(--ink2)] leading-relaxed mb-5">
          Heldall can bring things back when they matter. You're in control of when and how.
        </p>
        <div className="flex flex-col gap-2">
          <button
            data-testid="push-prompt-enable"
            onClick={turnOn}
            disabled={busy}
            className="w-full rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] disabled:opacity-50 text-[var(--on-pine)] font-semibold py-3 text-sm shadow-[0_8px_24px_rgba(44,90,77,0.25)] transition-all duration-200"
          >
            Turn on notifications
          </button>
          <button
            data-testid="push-prompt-dismiss"
            onClick={notNow}
            className="w-full rounded-full text-[var(--ink3)] hover:text-[var(--ink2)] font-semibold py-2.5 text-sm transition-colors"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
